import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, ChevronDown, Trash2, Pencil } from 'lucide-react';
import {
  JOB_ISSUE_CATEGORIES,
  JOB_ISSUE_OPTIONS,
  JOB_ISSUE_SEVERITY_LABEL,
  JOB_ISSUE_RESPONSIBILITY_LABEL,
  type JobIssueCategory,
  type JobIssueSeverity,
  type JobIssueResponsibility,
} from '@iomi/shared';

// ── Image compression (same as PhotosNotesPage) ─────────────────────────────

const MAX_DIM = 1200;
const MAX_DATA_URL_LENGTH = 680_000;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

async function compressImage(file: File): Promise<string> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  let width = img.naturalWidth;
  let height = img.naturalHeight;
  if (width > MAX_DIM || height > MAX_DIM) {
    const scale = MAX_DIM / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  let quality = 0.7;
  for (;;) {
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_DATA_URL_LENGTH) return dataUrl;
    if (quality > 0.4) {
      quality = Math.max(0.4, quality - 0.1);
    } else {
      width = Math.round(width * 0.75);
      height = Math.round(height * 0.75);
      quality = 0.6;
      if (width < 100) return canvas.toDataURL('image/jpeg', 0.4);
    }
  }
}

// ── Types ────────────────────────────────────────────────────────────────────

interface PhotoPreview {
  imageData: string;
  fileName: string;
}

export interface IssueFormData {
  category: string;
  issue: string;
  severity: string;
  responsibility: string;
  notes: string;
  photos?: Array<{ imageData: string; fileName: string }>;
}

interface IssueLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: IssueFormData) => Promise<unknown>;
  /** Pre-fill data for view/edit mode */
  initialData?: {
    category: string;
    issue: string;
    severity: string;
    responsibility: string;
    notes: string;
    photos?: Array<{ url: string; fileName: string; id: string }>;
    loggedBy?: { displayName: string };
    timestamp?: string;
  };
  /** Start in read-only mode (view existing issue) */
  readOnly?: boolean;
}

// ── Categories as array for iteration ────────────────────────────────────────

const CATEGORIES = Object.entries(JOB_ISSUE_CATEGORIES) as [JobIssueCategory, string][];
const SEVERITIES = Object.entries(JOB_ISSUE_SEVERITY_LABEL) as [JobIssueSeverity, string][];
const RESPONSIBILITIES = Object.entries(JOB_ISSUE_RESPONSIBILITY_LABEL) as [JobIssueResponsibility, string][];

// ── Component ────────────────────────────────────────────────────────────────

export function IssueLogModal({ isOpen, onClose, onSubmit, initialData, readOnly: initialReadOnly }: IssueLogModalProps) {
  const [category, setCategory] = useState<JobIssueCategory | ''>('');
  const [issue, setIssue] = useState('');
  const [severity, setSeverity] = useState<JobIssueSeverity>('medium');
  const [responsibility, setResponsibility] = useState<JobIssueResponsibility | ''>('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isReadOnly, setIsReadOnly] = useState(initialReadOnly ?? false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Populate from initialData when opening
  useEffect(() => {
    if (isOpen && initialData) {
      setCategory((initialData.category as JobIssueCategory) || '');
      setIssue(initialData.issue || '');
      setSeverity((initialData.severity as JobIssueSeverity) || 'medium');
      setResponsibility((initialData.responsibility as JobIssueResponsibility) || '');
      setNotes(initialData.notes || '');
      setPhotos([]);
      setIsReadOnly(initialReadOnly ?? false);
    } else if (isOpen && !initialData) {
      // Reset for create mode
      setCategory('');
      setIssue('');
      setSeverity('medium');
      setResponsibility('');
      setNotes('');
      setPhotos([]);
      setIsReadOnly(false);
    }
  }, [isOpen, initialData, initialReadOnly]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Auto-select issue when category only has one option
  useEffect(() => {
    if (category && !initialData) {
      const options = JOB_ISSUE_OPTIONS[category];
      if (options.length === 1) {
        setIssue(options[0]);
      } else {
        setIssue('');
      }
    }
  }, [category, initialData]);

  const issueOptions = category ? JOB_ISSUE_OPTIONS[category as JobIssueCategory] ?? [] : [];
  const isValid = category && issue && severity && responsibility && notes.trim();
  const isCreateMode = !initialData;
  const title = isCreateMode ? 'Log Issue' : isReadOnly ? 'Issue Details' : 'Edit Issue';

  async function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const imageData = await compressImage(file);
      setPhotos(prev => [...prev, { imageData, fileName: file.name }]);
    } catch {
      // Ignore compression failures
    }
  }

  async function handleSubmit() {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        category,
        issue,
        severity,
        responsibility,
        notes: notes.trim(),
        photos: photos.length > 0 ? photos : undefined,
      });
      // Reset and close
      setCategory('');
      setIssue('');
      setSeverity('medium');
      setResponsibility('');
      setNotes('');
      setPhotos([]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log issue');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  const timestamp = initialData?.timestamp
    ? new Date(initialData.timestamp).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone shrink-0">
          <button type="button" onClick={onClose} disabled={submitting}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-slate hover:bg-mist transition-colors">
            <X size={18} />
          </button>
          <h2 className="font-display text-[15px] font-semibold text-midnight">{title}</h2>
          {isReadOnly ? (
            <button
              type="button"
              onClick={() => setIsReadOnly(false)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-plum hover:bg-plum/10 transition-colors"
            >
              <Pencil size={16} />
            </button>
          ) : (
            <div className="w-9" />
          )}
        </div>

        {/* Scrollable form */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="flex flex-col gap-5 max-w-lg mx-auto">

            {error && (
              <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose">
                {error}
              </div>
            )}

            {/* Meta info (view/edit mode only) */}
            {initialData && (
              <div className="flex flex-col gap-1 text-[13px] text-slate">
                {initialData.loggedBy && (
                  <p><span className="font-medium text-midnight">Logged by:</span> {initialData.loggedBy.displayName}</p>
                )}
                {timestamp && (
                  <p><span className="font-medium text-midnight">Time:</span> {timestamp}</p>
                )}
              </div>
            )}

            {/* Category */}
            <Field label="Category" required>
              {isReadOnly ? (
                <div className="h-11 rounded-xl border border-stone bg-mist/50 px-3 flex items-center text-small text-midnight">
                  {JOB_ISSUE_CATEGORIES[category as JobIssueCategory] ?? (category || '—')}
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value as JobIssueCategory)}
                    className="w-full h-11 rounded-xl border border-stone bg-white px-3 pr-9 text-small text-midnight
                               appearance-none focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20"
                  >
                    <option value="" disabled>Select category</option>
                    {CATEGORIES.map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
                </div>
              )}
            </Field>

            {/* Issue */}
            {category && issueOptions.length > 1 && (
              <Field label="Issue" required>
                {isReadOnly ? (
                  <div className="h-11 rounded-xl border border-stone bg-mist/50 px-3 flex items-center text-small text-midnight">
                    {issue || '—'}
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={issue}
                      onChange={e => setIssue(e.target.value)}
                      className="w-full h-11 rounded-xl border border-stone bg-white px-3 pr-9 text-small text-midnight
                                 appearance-none focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20"
                    >
                      <option value="" disabled>Select issue</option>
                      {issueOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
                  </div>
                )}
              </Field>
            )}

            {/* Issue - auto-selected single option display */}
            {category && issueOptions.length === 1 && (
              <Field label="Issue" required>
                <div className="h-11 rounded-xl border border-stone bg-mist/50 px-3 flex items-center text-small text-midnight">
                  {issueOptions[0]}
                </div>
              </Field>
            )}

            {/* Severity */}
            <Field label="Severity" required>
              <div className="flex gap-2">
                {SEVERITIES.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={isReadOnly ? undefined : () => setSeverity(key)}
                    className={`flex-1 h-11 rounded-xl border text-[13px] font-medium transition-colors
                      ${isReadOnly ? 'cursor-default' : ''}
                      ${severity === key
                        ? key === 'high'
                          ? 'bg-rose/10 border-rose text-rose'
                          : key === 'medium'
                            ? 'bg-amber-50 border-amber-400 text-amber-700'
                            : 'bg-emerald-50 border-emerald-400 text-emerald-700'
                        : isReadOnly
                          ? 'border-stone text-slate/40'
                          : 'border-stone text-slate hover:bg-mist'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>

            {/* Responsibility */}
            <Field label="Responsibility" required>
              <div className="grid grid-cols-2 gap-2">
                {RESPONSIBILITIES.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={isReadOnly ? undefined : () => setResponsibility(key)}
                    className={`h-11 rounded-xl border text-[13px] font-medium transition-colors
                      ${isReadOnly ? 'cursor-default' : ''}
                      ${responsibility === key
                        ? 'bg-plum/10 border-plum text-plum'
                        : isReadOnly
                          ? 'border-stone text-slate/40'
                          : 'border-stone text-slate hover:bg-mist'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>

            {/* Notes */}
            <Field label="Notes" required>
              {isReadOnly ? (
                <div className="rounded-xl border border-stone bg-mist/50 px-3 py-2.5 text-small text-midnight whitespace-pre-wrap leading-relaxed min-h-[80px]">
                  {notes || '—'}
                </div>
              ) : (
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Describe the issue..."
                  rows={4}
                  className="w-full rounded-xl border border-stone bg-white px-3 py-2.5 text-small text-midnight resize-none
                             focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                             placeholder:text-slate/60"
                />
              )}
            </Field>

            {/* Photos */}
            {(isReadOnly ? (initialData?.photos?.length ?? 0) > 0 : true) && (
              <Field label="Photos" optional={!isReadOnly}>
                {isReadOnly ? (
                  <div className="grid grid-cols-3 gap-2">
                    {initialData?.photos?.map(photo => (
                      <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-mist border border-stone">
                        <img src={photo.url} alt={photo.fileName} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {photos.map((photo, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-stone">
                          <img src={photo.imageData} alt={photo.fileName} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                            className="absolute top-0.5 right-0.5 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center
                                       text-white hover:bg-black/70 transition-colors"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      ))}
                      {photos.length < 5 && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-20 h-20 rounded-lg border-2 border-dashed border-stone flex flex-col items-center justify-center gap-1
                                     text-slate hover:border-plum hover:text-plum transition-colors"
                        >
                          <Camera size={18} />
                          <span className="text-[10px] font-medium">Add</span>
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      className="hidden"
                    />
                  </>
                )}
              </Field>
            )}

          </div>
        </div>

        {/* Bottom submit button (create/edit mode only) */}
        {!isReadOnly && (
          <div className="px-4 py-3 border-t border-stone shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              className="w-full h-11 rounded-xl bg-plum text-white text-[14px] font-semibold
                         disabled:opacity-40 hover:bg-rose active:opacity-80 transition-colors"
            >
              {submitting ? 'Saving...' : isCreateMode ? 'Log Issue' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ── Field wrapper ────────────────────────────────────────────────────────────

function Field({ label, required, optional, children }: {
  label: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-bold uppercase tracking-widest text-slate">
        {label}
        {required && <span className="text-rose ml-0.5">*</span>}
        {optional && <span className="ml-1 text-slate/50 normal-case font-normal tracking-normal">(optional)</span>}
      </label>
      {children}
    </div>
  );
}
